import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  Renderer2,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { HeaderWrapperService } from '@amfam/shared/ui/ds-header/lib/services/header-wrapper.service';

@Component({
  selector: 'ds-roofline',
  templateUrl: './ds-roofline.component.html',
  styleUrls: ['./ds-roofline.component.scss'],
  host: {
    '(window:resize)': 'onResize()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DsRooflineComponent implements AfterViewInit {
  private headerWrapperService = inject(HeaderWrapperService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  readonly loggedIn = input.required<boolean | null>();
  private readonly loggedIn$ = toObservable(this.loggedIn);
  readonly roofpeak = viewChild.required<ElementRef<HTMLInputElement>>('roofpeak');
  readonly mover = viewChild.required<ElementRef>('mover');

  private leftOffsetPosition!: number;
  private innerWidth!: number;
  private linkActive: ElementRef | null = null;

  onResize() {
    this.innerWidth = document.documentElement.clientWidth;
  }

  ngAfterViewInit() {
    this.innerWidth = document.documentElement.clientWidth;

    this.headerWrapperService.loaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Recheck active link position after loaded
      this.moveRoofline();
    });

    combineLatest([this.headerWrapperService.headerNavRect$, this.loggedIn$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([headerNavRect]) => !!headerNavRect)
      )
      .subscribe(([headerNavRect, loggedIn]) => {
        const roofpeak = this.roofpeak();
        if (loggedIn) {
          this.renderer.addClass(roofpeak.nativeElement, 'roofpeak');
        } else {
          this.renderer.removeClass(roofpeak.nativeElement, 'roofpeak');
        }

        const rooflineImgWidth = roofpeak.nativeElement.getBoundingClientRect().width;
        const headerNavRectWidth = headerNavRect.width ?? this.innerWidth;
        this.leftOffsetPosition =
          rooflineImgWidth / 2 + Math.max((this.innerWidth - headerNavRectWidth) / 2, 0);

        this.moveRoofline();
      });
  }

  private setMoverWidth(link: ElementRef | null) {
    if (!link?.nativeElement) return;
    const rect = link?.nativeElement.getBoundingClientRect();
    this.mover().nativeElement.style.width = `${rect.left - this.leftOffsetPosition + rect.width / 2}px`;
  }

  private moveRoofline() {
    this.headerWrapperService.linkActive$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(linkActive => linkActive?.nativeElement)
      )
      .subscribe(linkActive => {
        this.linkActive = linkActive;
        this.setMoverWidth(linkActive);
      });

    this.headerWrapperService.linkFocus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(linkFocus => {
        this.setMoverWidth(linkFocus || this.linkActive);
      });
  }
}
